import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { getPreparers } from '../services/api';
import type { AvailablePreparer } from '../services/api';

export type { AvailablePreparer };

interface ResolvedPreparerResult {
  /** The resolved preparer ID matched from actual tax return data */
  resolvedPreparerId: string | undefined;
  /** List of unique preparers extracted from tax return data */
  availablePreparers: AvailablePreparer[];
  /** Whether the current user info is still loading */
  isLoading: boolean;
  /** Whether the preparer resolution is still in progress (user loaded, but preparers not yet fetched) */
  isResolving: boolean;
  /** Raw values from useCurrentUser for convenience */
  preparerId: string | undefined;
  preparerName: string | undefined;
  azureEmail: string | undefined;
  systemUser: ReturnType<typeof useCurrentUser>['systemUser'];
  isAdmin: boolean;
}

/**
 * Resolves the current user's preparer ID using the token-provided systemUserId.
 * Fetches available preparers from the dedicated /api/preparers endpoint
 * instead of loading all tax returns.
 *
 * @param taxYear - The tax year to fetch preparers for
 */
export function useResolvedPreparerId(taxYear: number | undefined): ResolvedPreparerResult {
  const {
    preparerId: rawPreparerId,
    preparerName,
    azureEmail,
    systemUser,
    isLoading: userLoading,
    isAdmin,
  } = useCurrentUser();

  // Normalize preparerId to lowercase for consistent GUID comparison
  const preparerId = rawPreparerId?.toLowerCase();

  // Fetch available preparers from the dedicated endpoint
  const { data: fetchedPreparers, isLoading: preparersLoading } = useQuery<AvailablePreparer[]>({
    queryKey: ['preparers', taxYear],
    queryFn: () => getPreparers({ taxYear }),
    enabled: !userLoading && !!taxYear,
  });

  // Normalize preparer IDs to lowercase
  const availablePreparers = (fetchedPreparers || []).map(p => ({
    ...p,
    id: p.id.toLowerCase(),
  })).sort((a, b) => a.name.localeCompare(b.name));

  // The preparerId from the token IS the resolved ID — no three-tier fallback needed
  const resolvedPreparerId = preparerId;

  return {
    resolvedPreparerId,
    availablePreparers,
    isLoading: userLoading,
    isResolving: !userLoading && preparersLoading,
    preparerId,
    preparerName,
    azureEmail,
    systemUser,
    isAdmin,
  };
}

export default useResolvedPreparerId;
